import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Checkbox,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Textarea,
  Tooltip,
  Spinner,
} from "@nextui-org/react";
import { Select, SelectItem } from "@nextui-org/select";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@nextui-org/modal";

import { EditIcon } from '../assets/icons/EditIcon';
import { DeleteIcon } from '../assets/icons/DeleteIcon';
import { getTypedFormData } from '../utils/TypeFormData';
import AttributesPageViewModel, { AttributesBusyStateKey } from '../viewModels/AttributesPageViewModel';
import { Attribute } from '../models/Attribute';

import { NormalizationEnum } from '../constants/normalizations';
import { ColumnSize } from "@react-types/table";
import toast, { Toaster } from 'react-hot-toast';

const columns: { key: string; label: string; width: ColumnSize | null }[] = [
  {
    key: "name",
    label: "Name",
    width: null
  },
  // {
  //   key: "description",
  //   label: "Description",
  //   width: null
  // },
  {
    key: "normalization",
    label: "Normalization",
    width: 100
  },
  {
    key: "is_outcome",
    label: "Is Outcome",
    width: 100
  },
  {
    key: "actions",
    label: "Actions",
    width: 80
  },
];

type FormData = {
  id: string;
  name: string;
  description: string;
  normalization: string;
  is_outcome: string;
}

const AttributesPage: React.FC = observer(() => {
  const [viewModel] = useState(() => new AttributesPageViewModel());
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    viewModel.fetchAttributes();
  }, [viewModel]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = getTypedFormData<FormData>(event.currentTarget);
    const id = formData.get('id') || undefined;

    viewModel.saveAttribute({
      id,
      name: formData.get('name'),
      description: formData.get('description'),
      normalization: formData.get('normalization') as NormalizationEnum,
      is_outcome: formData.get('is_outcome') === 'true'
    }).then(() => {
      if (id) {
        toast.success('Attribute updated');
      } else {
        toast.success('Attribute added');
      }
      setOpenDialog(false);
    }).catch(() => {
      toast.error('Failed to add attribute');
    });
  }

  const handleDeleteAttribute = (id: string) => {
    viewModel.deleteAttribute(id);
  }

  const renderCell = (attribute: Attribute, columnKey: React.Key) => {
    const cellValue = attribute[columnKey as keyof Attribute];

    switch (columnKey) {
      case "name":
        return (
          <div className="flex flex-col">
            <span className="text-sm">{cellValue}</span>
            <span className="text-tiny text-default-500">{attribute.description}</span>
          </div>
        );
      case "is_outcome":
        return (
          <span className="text-sm">{cellValue ? 'Yes' : 'No'}</span>
        );
      case "actions":
        return (
          <div className="relative flex items-center gap-2">
            <Tooltip content="Edit">
              <span onClick={() => {
                viewModel.setCurrentAttribute(attribute);
                setOpenDialog(true);
              }} className="text-lg text-default-400 cursor-pointer active:opacity-50">
                <EditIcon />
              </span>
            </Tooltip>
            <Tooltip color="danger" content="Delete">
              <span onClick={() => {
                if (attribute.id) {
                  handleDeleteAttribute(attribute.id)
                }
              }} className="text-lg text-danger cursor-pointer active:opacity-50">
                <DeleteIcon />
              </span>
            </Tooltip>
          </div>
        );
      default:
        return cellValue;
    }
  }

  const topContent = React.useMemo(() => {
    return (
      <div className="flex w-full">
        <Button
          className='w-fit'
          color="primary"
          size="md"
          onPress={() => setOpenDialog(true)}
        >Add Attribute</Button>
      </div>
    )
  }, [])

  if (viewModel.error) {
    return (
      <div className='grid place-items-center place-content-center'>
        <p className='text-danger'>{viewModel.error}</p>
      </div>
    );
  }

  return (
    <div className='flex-1 p-8 max-w-[800px] mx-auto'>
      <div className='flex flex-col h-full'>
        <Toaster />
        <Modal
          isOpen={openDialog}
          placement="top-center"
          onClose={() => setOpenDialog(false)}
        >
          <ModalContent>
            {() => (
              <form onSubmit={handleSubmit}>
                <ModalHeader className="flex flex-col gap-1">{viewModel.currentAttribute.id ? 'Edit Attribute' : 'Add Attribute'}</ModalHeader>
                <ModalBody>
                  <Input
                    className='hidden'
                    name="id"
                    hidden
                    type="text"
                    defaultValue={viewModel.currentAttribute.id?.toString()}
                  />
                  <Input
                    name="name"
                    label="Attribute Name"
                    type="text"
                    required
                    isRequired
                    defaultValue={viewModel.currentAttribute.name}
                  />
                  <Textarea
                    name="description"
                    label="Description"
                    type="text"
                    defaultValue={viewModel.currentAttribute.description}
                  />
                  <Select
                    name="normalization"
                    label="Normalization"
                    defaultSelectedKeys={[viewModel.currentAttribute.normalization]}
                  >
                    {Object.values(NormalizationEnum).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </Select>
                  <Checkbox
                    name="is_outcome"
                    defaultSelected={!!viewModel.currentAttribute.is_outcome}
                    value="true"
                  >Is outcome</Checkbox>
                </ModalBody>
                <ModalFooter>
                  <Button color="danger" variant="flat" onPress={() => setOpenDialog(false)}>
                    Close
                  </Button>
                  <Button color="primary" type="submit" isLoading={viewModel.isLoading[AttributesBusyStateKey.SAVE_ATTRIBUTE]}>
                    Save
                  </Button>
                </ModalFooter>
              </form>
            )}
          </ModalContent>
        </Modal>

        {viewModel.isLoading[AttributesBusyStateKey.FETCH_ATTRIBUTES] || viewModel.isLoading[AttributesBusyStateKey.SAVE_ATTRIBUTE] || viewModel.isLoading[AttributesBusyStateKey.DELETE_ATTRIBUTE] ? (
          <div className='w-full flex items-center justify-center'>
            <Spinner />
          </div>
        ) : (
          <Table
            aria-label="Example table with dynamic content"
            isHeaderSticky
            layout='fixed'
            classNames={{
              base: "overflow-hidden p-1"
            }}
            topContent={topContent}
          >
            <TableHeader columns={columns}>
              {(column) => <TableColumn key={column.key} width={column.width}>{column.label}</TableColumn>}
            </TableHeader>
            <TableBody items={viewModel.attributes}>
              {(item) => (
                <TableRow key={item.id}>
                  {(columnKey) => (
                    <TableCell
                      className={columnKey === "name" ? "truncate" : ""}
                    >
                      {renderCell(item, columnKey)}
                    </TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
});

export default AttributesPage;